import { sessionSocketListeners } from '@/lib/session-socket-listeners';
import { useSessionBlocked } from '@/stores/sessionTransition';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSameActionMutation } from '@/hooks/useSingleFlightMutation';
import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  connectWhatsApp,
  getWhatsAppAutomationRisk,
  getWhatsAppStatus,
  disconnectWhatsApp,
  migrateToUazapi,
  rollbackWhatsAppMigration,
  finalizeWhatsAppMigration,
} from '@/services/whatsapp';
import { getExistingSocket } from '@/lib/socket';

const WHATSAPP_KEY = 'whatsapp-status';

export function useWhatsAppStatus(enabled = true) {
  const paused = useSessionBlocked();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (paused) return;
    const socket = getExistingSocket();
    if (!socket) return;
    const listeners = sessionSocketListeners(socket);

    const handleStatusChanged = () => {
      queryClient.invalidateQueries({ queryKey: [WHATSAPP_KEY] });
    };

    listeners.on('whatsapp:status_changed', handleStatusChanged);
    return () => {
      listeners.off('whatsapp:status_changed', handleStatusChanged);
    };
  }, [queryClient, paused]);

  return useQuery({
    queryKey: [WHATSAPP_KEY],
    queryFn: getWhatsAppStatus,
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'connecting') return 3000;
      if (status === 'disconnected') return 30000;
      return false;
    },
  });
}

/** RC-209 — banner do shell. Atualiza quando a conexão muda (mesmo evento do status). */
export function useWhatsAppAutomationRisk(enabled = true) {
  const paused = useSessionBlocked();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (paused || !enabled) return;
    const socket = getExistingSocket();
    if (!socket) return;
    const listeners = sessionSocketListeners(socket);
    const handleStatusChanged = () => {
      queryClient.invalidateQueries({ queryKey: [WHATSAPP_KEY, 'automation-risk'] });
    };
    listeners.on('whatsapp:status_changed', handleStatusChanged);
    return () => {
      listeners.off('whatsapp:status_changed', handleStatusChanged);
    };
  }, [queryClient, paused, enabled]);

  return useQuery({
    queryKey: [WHATSAPP_KEY, 'automation-risk'],
    queryFn: getWhatsAppAutomationRisk,
    enabled,
    staleTime: 60_000,
    refetchInterval: (query) => (query.state.data?.disconnected ? 60_000 : 5 * 60_000),
  });
}

export function useConnectWhatsApp() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: connectWhatsApp,
    onSuccess: (data) => {
      queryClient.setQueryData([WHATSAPP_KEY], data);
      if (data.status === 'disconnected') {
        toast.error('Não foi possível gerar o QR Code agora. Tente novamente em instantes ou acione o suporte.');
      }
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message;
      toast.error(
        message && message !== 'Internal server error'
          ? message
          : 'Não foi possível gerar o QR Code agora. Tente novamente em instantes ou acione o suporte.',
      );
      queryClient.invalidateQueries({ queryKey: [WHATSAPP_KEY] });
    },
  });
}

export function useDisconnectWhatsApp() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: disconnectWhatsApp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WHATSAPP_KEY] });
      toast.success('WhatsApp desconectado');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message || 'Erro ao desconectar WhatsApp');
    },
  });
}

function migrationErrorMessage(error: unknown, fallback: string): string {
  const message = (error as AxiosError<{ message?: string | string[] }>)?.response?.data?.message;
  if (Array.isArray(message)) return message.join(' ');
  return typeof message === 'string' && message ? message : fallback;
}

/** Starts the UAZAPI migration. The backend reports each recovery phase in WhatsAppStatus. */
export function useMigrateToUazapi() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: migrateToUazapi,
    onSuccess: (data) => {
      if ('status' in data) queryClient.setQueryData([WHATSAPP_KEY], data);
      queryClient.invalidateQueries({ queryKey: [WHATSAPP_KEY] });
      queryClient.invalidateQueries({ queryKey: ['inbox', 'state'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error) => toast.error(migrationErrorMessage(error, 'Não foi possível iniciar a troca de versão.')),
  });
}

export function useRollbackWhatsAppMigration() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: rollbackWhatsAppMigration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WHATSAPP_KEY] });
      queryClient.invalidateQueries({ queryKey: ['inbox', 'state'] });
      toast.success('Voltamos para a versão anterior. Conecte o WhatsApp de novo.');
    },
    onError: (error) => toast.error(migrationErrorMessage(error, 'Não foi possível voltar para a versão anterior.')),
  });
}

export function useFinalizeWhatsAppMigration() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: finalizeWhatsAppMigration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WHATSAPP_KEY] });
      toast.success('Troca de versão concluída.');
    },
    onError: (error) => toast.error(migrationErrorMessage(error, 'Não foi possível concluir a troca de versão.')),
  });
}
